// review cycle types for admin-growth module

export interface ReviewCycleApiData {
	id: string;
	company_id: string;
	notification_cadence: string;
	next_review_date: string | null;
	active: boolean;
	created_at: string;
	updated_at: string;
}

export interface ReviewCycle {
	id: string;
	companyId: string;
	notificationCadence: string;
	nextReviewDate: Date | null;
	active: boolean;
	createdAt: string;
	updatedAt: string;
}

/**
 * Available review cadence options for performance cycles
 * These are the only cadences that can be selected in the admin UI
 */
export const REVIEW_CADENCE_OPTIONS = ['quarterly', 'biannual', 'annual'] as const;

/**
 * Type for review cadence options
 */
export type ReviewCadenceOption = (typeof REVIEW_CADENCE_OPTIONS)[number];

/**
 * Formats a cadence string for display with proper capitalization and terminology
 * @param cadence - The raw cadence string (e.g., "quarterly", "biannual", "weekly")
 * @returns Formatted cadence string (e.g., "Quarterly", "Semiannual", "Weekly")
 */
export const formatReviewCadence = (cadence: string): string => {
	const lowerCadence = cadence.toLowerCase();

	switch (lowerCadence) {
		case 'biannual':
		case 'bi-annual':
		case 'semiannual':
		case 'semi-annual':
			return 'Semiannual';
		case 'quarterly':
			return 'Quarterly';
		case 'monthly':
			return 'Monthly';
		case 'biweekly':
		case 'bi-weekly':
			return 'Biweekly';
		case 'weekly':
			return 'Weekly';
		case 'annual':
		case 'annually':
			return 'Annual';
		default:
			// Capitalize first letter for unknown cadences
			return cadence.charAt(0).toUpperCase() + cadence.slice(1).toLowerCase();
	}
};

export class ReviewCycleTransformer {
	static fromApiData(apiData: ReviewCycleApiData): ReviewCycle {
		let nextReviewDate: Date | null = null;
		if (apiData.next_review_date) {
			const parsedDate = new Date(apiData.next_review_date);
			nextReviewDate = isNaN(parsedDate.getTime()) ? null : parsedDate;
		}

		return {
			id: apiData.id,
			companyId: apiData.company_id,
			notificationCadence: apiData.notification_cadence,
			nextReviewDate,
			active: apiData.active,
			createdAt: apiData.created_at,
			updatedAt: apiData.updated_at,
		};
	}

	static toApiData(reviewCycle: Partial<ReviewCycle>): Partial<ReviewCycleApiData> {
		const apiData: Partial<ReviewCycleApiData> = {};

		if (reviewCycle.notificationCadence !== undefined) {
			apiData.notification_cadence = reviewCycle.notificationCadence;
		}

		if (reviewCycle.nextReviewDate !== undefined) {
			apiData.next_review_date = reviewCycle.nextReviewDate?.toISOString() || null;
		}

		if (reviewCycle.active !== undefined) {
			apiData.active = reviewCycle.active;
		}

		return apiData;
	}
}
